import { useCallback, useEffect, useMemo, useState } from 'react';
import { autoplannerAPI } from '../../../services/api';

/**
 * useValidationExclusions
 * Manages session exclusions per crew with hydration and background validation triggers.
 *
 * @param {Object} params
 * @param {Object|null} params.autoplannerTask
 * @param {(task: Object)=>void} params.setAutoplannerTask
 * @param {number|string} params.taskId
 * @param {()=>Promise<void>|(()=>void)} params.triggerBackgroundValidation
 * @param {(msg: {type: 'success'|'error', message: string})=>void} [params.notify]
 */
export default function useValidationExclusions({
    autoplannerTask,
    setAutoplannerTask,
    taskId,
    triggerBackgroundValidation,
    notify,
}) {
    const [sessionExclusions, setSessionExclusions] = useState({});
    const [isUpdatingCrew, setIsUpdatingCrew] = useState({});

    const refreshAutoplannerTask = useCallback(async () => {
        try {
            const currentTaskId = autoplannerTask?.id || taskId;
            if (!currentTaskId) return null;
            const updatedTask = await autoplannerAPI.getAutoplannerTask(currentTaskId);
            if (updatedTask && setAutoplannerTask) {
                setAutoplannerTask(updatedTask);
            }
            return updatedTask;
        } catch (e) {
            return null;
        }
    }, [autoplannerTask?.id, setAutoplannerTask, taskId]);

    const loadSessionExclusions = useCallback(() => {
        try {
            if (autoplannerTask?.session_exclusions) {
                const backendExclusions = JSON.parse(autoplannerTask.session_exclusions);
                setSessionExclusions(backendExclusions || {});
            } else {
                setSessionExclusions({});
            }
        } catch (e) {
            setSessionExclusions({});
        }
    }, [autoplannerTask]);

    useEffect(() => {
        if (autoplannerTask) {
            loadSessionExclusions();
        }
    }, [autoplannerTask, loadSessionExclusions]);

    const isSessionExcluded = useCallback(
        (crewId, sessionId) => {
            const localExclusions = sessionExclusions[crewId] || [];
            if (localExclusions.includes(sessionId)) return true;
            try {
                const backendExclusions = autoplannerTask?.session_exclusions
                    ? JSON.parse(autoplannerTask.session_exclusions)
                    : {};
                const backendCrewExclusions = backendExclusions[crewId] || [];
                return backendCrewExclusions.includes(sessionId);
            } catch (e) {
                return false;
            }
        },
        [autoplannerTask, sessionExclusions]
    );

    const excludeSession = useCallback(
        async (crewId, sessionId) => {
            try {
                const currentTaskId = autoplannerTask?.id || taskId;
                if (!currentTaskId) return;
                const result = await autoplannerAPI.excludeSessionForCrew(currentTaskId, crewId, sessionId);
                if (result?.success) {
                    setSessionExclusions((prev) => ({
                        ...prev,
                        [crewId]: [...(prev[crewId] || []), sessionId],
                    }));
                    await refreshAutoplannerTask();
                    triggerBackgroundValidation && triggerBackgroundValidation();
                } else if (notify) {
                    notify({ type: 'error', message: `Failed to exclude session: ${result?.error || 'Unknown error'}` });
                }
            } catch (e) {
                if (notify) notify({ type: 'error', message: 'Network error while excluding session. Please try again.' });
            }
        },
        [autoplannerTask?.id, taskId, notify, refreshAutoplannerTask, triggerBackgroundValidation]
    );

    const includeSession = useCallback(
        async (crewId, sessionId) => {
            try {
                const currentTaskId = autoplannerTask?.id || taskId;
                if (!currentTaskId) return;
                const result = await autoplannerAPI.includeSessionForCrew(currentTaskId, crewId, sessionId);
                if (result?.success) {
                    setSessionExclusions((prev) => ({
                        ...prev,
                        [crewId]: (prev[crewId] || []).filter((id) => id !== sessionId),
                    }));
                    await refreshAutoplannerTask();
                    triggerBackgroundValidation && triggerBackgroundValidation();
                } else if (notify) {
                    notify({ type: 'error', message: `Failed to include session: ${result?.error || 'Unknown error'}` });
                }
            } catch (e) {
                if (notify) notify({ type: 'error', message: 'Network error while including session. Please try again.' });
            }
        },
        [autoplannerTask?.id, taskId, notify, refreshAutoplannerTask, triggerBackgroundValidation]
    );

    const excludeCrewAllSessions = useCallback(
        async (crewId, sessionIds = []) => {
            try {
                const currentTaskId = autoplannerTask?.id || taskId;
                if (!currentTaskId) return;
                setIsUpdatingCrew((prev) => ({ ...prev, [crewId]: true }));
                const uniqueIds = Array.from(new Set((sessionIds || []).filter(Boolean)));
                const toExclude = uniqueIds.filter((sid) => !isSessionExcluded(crewId, sid));
                if (toExclude.length === 0) {
                    setIsUpdatingCrew((prev) => ({ ...prev, [crewId]: false }));
                    return;
                }
                await autoplannerAPI.excludeSessionsForCrew(currentTaskId, crewId, toExclude);
                setSessionExclusions((prev) => ({
                    ...prev,
                    [crewId]: Array.from(new Set([...(prev[crewId] || []), ...toExclude])),
                }));
                await refreshAutoplannerTask();
                triggerBackgroundValidation && triggerBackgroundValidation();
            } catch (e) {
                if (notify) notify({ type: 'error', message: 'Failed to exclude crew for all sessions' });
            } finally {
                setIsUpdatingCrew((prev) => ({ ...prev, [crewId]: false }));
            }
        },
        [autoplannerTask?.id, taskId, isSessionExcluded, notify, refreshAutoplannerTask, triggerBackgroundValidation]
    );

    const includeCrewAllSessions = useCallback(
        async (crewId, sessionIds = []) => {
            try {
                const currentTaskId = autoplannerTask?.id || taskId;
                if (!currentTaskId) return;
                setIsUpdatingCrew((prev) => ({ ...prev, [crewId]: true }));
                const uniqueIds = Array.from(new Set((sessionIds || []).filter(Boolean)));
                const toInclude = uniqueIds.filter((sid) => isSessionExcluded(crewId, sid));
                if (toInclude.length === 0) {
                    setIsUpdatingCrew((prev) => ({ ...prev, [crewId]: false }));
                    return;
                }
                await autoplannerAPI.includeSessionsForCrew(currentTaskId, crewId, toInclude);
                setSessionExclusions((prev) => ({
                    ...prev,
                    [crewId]: (prev[crewId] || []).filter((sid) => !toInclude.includes(sid)),
                }));
                await refreshAutoplannerTask();
                triggerBackgroundValidation && triggerBackgroundValidation();
            } catch (e) {
                if (notify) notify({ type: 'error', message: 'Failed to include crew for all sessions' });
            } finally {
                setIsUpdatingCrew((prev) => ({ ...prev, [crewId]: false }));
            }
        },
        [autoplannerTask?.id, taskId, isSessionExcluded, notify, refreshAutoplannerTask, triggerBackgroundValidation]
    );

    return {
        sessionExclusions,
        setSessionExclusions,
        isSessionExcluded,
        isUpdatingCrew,
        loadSessionExclusions,
        excludeSession,
        includeSession,
        excludeCrewAllSessions,
        includeCrewAllSessions,
        refreshAutoplannerTask,
    };
}


